import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { css } from "@emotion/core";
import { colors } from "styling/variables";
import { toRgba } from "styling/utils";

const overlayStyle = css`
  background-color: ${toRgba(colors.mineShaft, 0.9)};
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  position: fixed;
  transition: top 0.35s, bottom 0.35s;
  z-index: 9999;
`;

const coverStyle = css`
  position: absolute;
  height: 100%;
  width: 100%;
`;

const stickyStyle = props => css`
  position: sticky;
  top: 0;
  height: ${props.height};
`;

const LoaderContainer = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  @media print {
    display: none;
  }
  ${props => {
    switch (props.mode) {
      case "overlay":
        return overlayStyle;
      case "cover":
        return coverStyle;
      case "sticky":
        return stickyStyle({ height: props.height });
      default:
        return null;
    }
  }};
  ${props => {
    return props.styles;
  }};
`;

class Loader extends PureComponent {
  state = {
    show: false
  };

  timeout = null;

  componentDidMount() {
    if (this.props.isLoading) this.show();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isLoading && !prevProps.isLoading) this.show();
    else if (!this.props.isLoading && prevProps.isLoading) this.hide();
  }

  // in case the component gets unmounted before loader is shown
  // avoid setState after unmount
  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  show() {
    this.timeout = setTimeout(() => {
      this.setState({ show: true });
    }, this.props.delay);
  }

  hide() {
    clearTimeout(this.timeout);
    this.setState({ show: false });
  }

  render() {
    return (
      this.state.show && (
        <LoaderContainer
          mode={this.props.mode}
          height={this.props.height}
          styles={this.props.styles}
        >
          {this.props.children}
        </LoaderContainer>
      )
    );
  }
}

Loader.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["normal", "overlay", "cover", "sticky"]),

  /** loader is shown with a small delay (from the point the parent sets visible to true)
   * in order to avoid showing and hiding instantly in fast networks.
   * Set delay to 0, to show instantly */
  delay: PropTypes.number,

  /** container height */
  height: PropTypes.string,

  /** override styles */
  styles: PropTypes.object
};

Loader.defaultProps = {
  mode: "normal",
  delay: 200,
  height: "600px"
};

export default Loader;
